import React, { useCallback, useEffect, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { LenderFieldMap, PortalConfiguration } from '@trnsact/trnsact-shared-types';
import {
  LenderMappingsFormValues,
  StructuredMappings,
} from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/types';
import { useDynamicLenderSubmissionDlmEngine } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/hooks/useDynamicLenderSubmissionDlmEngine';
import { useDlmRulesEventsHandler } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/hooks/useDlmRulesEventsHandler';
import { LenderProfile } from 'types';
import { useFormContext } from 'react-hook-form';
import {
  getLenderMappingsFormDefaultValues,
  isCategoryVisible,
  sanitizeFormKey,
} from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/lib';
import { CategorySection } from './CategorySection';
import { Engine } from 'json-rules-engine';

interface Props {
  lender: LenderProfile;
  portalConfiguration: PortalConfiguration;
  dynamicLenderMappings: LenderFieldMap[];
  structuredMappings: StructuredMappings;
  onMappingsChange: (updatedMappings: LenderFieldMap[]) => void;
  onCorpOnlyChange?: (corpOnly: boolean) => void;
}

export const LenderDataTab = ({
  lender,
  portalConfiguration,
  dynamicLenderMappings,
  structuredMappings,
  onMappingsChange,
  onCorpOnlyChange,
}: Props) => {
  const classes = useStyles();

  const { setValue, reset } = useFormContext<LenderMappingsFormValues>();

  useEffect(() => {
    reset(getLenderMappingsFormDefaultValues(dynamicLenderMappings));
  }, []);

  const engineRef = React.useRef<Engine | null>(null);

  // corpOnly state/ref deactivated along with the toggle due to a change of scope
  const [corpOnly, setCorpOnly] = useState(false);
  const corpOnlyOriginalHiddenRef = useRef<Record<string, boolean>>({});

  //console.log(dynamicLenderMappings)

  const setFormValue = useCallback(
    (lenderProperty: string, value: any) => {
      setValue(`mappingsByLenderProperty.${sanitizeFormKey(lenderProperty)}.mappedValue`, value);
    },
    [setValue]
  );

  const { handleDlmRulesEvents } = useDlmRulesEventsHandler({
    getEngine: () => engineRef.current!,
    dynamicLenderMappings,
    onMappingsChange,
    setFormValue,
  });

  const { dlmRulesEngine, runRulesEngine, isReady } = useDynamicLenderSubmissionDlmEngine(
    dynamicLenderMappings,
    handleDlmRulesEvents
  );

  engineRef.current = dlmRulesEngine;

  const handleValueChange = useCallback(
    (lenderProperty: string, newValue: any) => {
      const index = dynamicLenderMappings.findIndex(el => el.lenderProperty === lenderProperty);
      if (index !== -1) {
        dynamicLenderMappings[index].mappedValue = newValue;
      }
      //form.setValue(`mappingsByLenderProperty.${dynamicLenderMappings[index]?.lenderFieldId}.mappedValue`, newValue);
      runRulesEngine(lenderProperty, newValue);
      onMappingsChange([...dynamicLenderMappings]);
    },
    [dynamicLenderMappings, runRulesEngine, onMappingsChange]
  );

  // handleCorpOnlyToggle deactivated along with the toggle due to a change of scope
  const handleCorpOnlyToggle = useCallback(
    (checked: boolean) => {
      setCorpOnly(checked);

      if (engineRef && engineRef.current) {
        console.log(`engineRef and engineRef.current are available, setting CORP_ONLY fact to ${checked}`);
        runRulesEngine('CORP_ONLY', checked);
      }

      // dynamicLenderMappings.forEach(field => {
      //   if (!field.lenderProperty || !isPersonalGuarantorField(field)) return;

      //   if (checked) {
      //     corpOnlyOriginalHiddenRef.current[field.lenderProperty] = field.details?.hidden ?? false;
      //     field.mappedValue = null;
      //     field.details = { ...field.details, hidden: true };
      //     setFormValue(field.lenderProperty, null);
      //   } else {
      //     const originalHidden = corpOnlyOriginalHiddenRef.current[field.lenderProperty] ?? false;
      //     field.details = { ...field.details, hidden: originalHidden };
      //   }
      // });

      onMappingsChange([...dynamicLenderMappings]);
      // onCorpOnlyChange?.(checked);
    },
    [dynamicLenderMappings, onMappingsChange, runRulesEngine]
  );

  if (!isReady) {
    return null;
  }

  return (
    <Box className={classes.root}>
      {/* Corp Only toggle deactivated due to a change of scope */}
      <FormControlLabel
        control={<Switch checked={corpOnly} onChange={(_e, checked) => handleCorpOnlyToggle(checked)} />}
        label="Corp Only (do not send guarantor/owner info)"
      />
      <Typography variant="body2" fontWeight={500} fontSize={16}>
        Please confirm the following information before submitting
      </Typography>
      <Typography variant="body2">All fields highlighted in red must be provided</Typography>

      <Box className={classes.sectionsContainer}>
        <Stack gap={2}>
          {structuredMappings &&
            Object.entries(structuredMappings)
              .filter(([category, sections]) => isCategoryVisible(category, sections))
              .map(([category, sections]) => (
                <CategorySection
                  key={category}
                  category={category}
                  sections={sections}
                  lender={lender}
                  portalConfiguration={portalConfiguration}
                  onValueChange={handleValueChange}
                />
              ))}
        </Stack>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: '16px 0',
  },
  sectionsContainer: {
    padding: '24px 0',
  },
}));
